import type { TimeSeriesPoint } from '@/shared/charts/time-series-chart/TimeSeriesChart.types';
import { findIndexAtOrAfter } from '@/shared/charts/time-series-chart/points/findPointIndex';

/** First, lowest, highest and last point of a bucket. */
export const MAX_POINTS_PER_BUCKET = 4;

/** Whether the points already fit the buckets, so thinning could not make them fewer. */
export function fitsBucketBudget(pointCount: number, bucketCount: number): boolean {
  return pointCount <= Math.max(1, Math.floor(bucketCount)) * MAX_POINTS_PER_BUCKET + 2;
}

/**
 * Indexes of the points to draw from `points[fromIndex..toIndex)`, in time order. The time span is
 * cut into equal buckets, and each bucket keeps its first, lowest, highest and last point ("M4").
 *
 * Min and max keep short spikes visible. First and last make the line enter and leave each bucket
 * at the right value: without them, a peak followed by a gap in the data is drawn as a long false
 * slope instead of dropping back to the baseline.
 *
 * Points must be ascending by timestamp. A range that already fits is returned whole.
 */
export function selectMinMaxIndices(
  points: readonly TimeSeriesPoint[],
  fromIndex: number,
  toIndex: number,
  bucketCount: number
): number[] {
  const start = Math.max(0, fromIndex);
  const end = Math.min(points.length, toIndex);
  const count = end - start;

  if (count <= 0) {
    return [];
  }

  if (fitsBucketBudget(count, bucketCount)) {
    return Array.from({ length: count }, (_, offset) => start + offset);
  }

  const firstTimestamp = points[start].timestamp;
  const span = points[end - 1].timestamp - firstTimestamp;

  if (span <= 0) {
    return [start, end - 1];
  }

  // The range's own first and last point stay outside the buckets, so they are always kept.
  const innerStart = start + 1;
  const innerEnd = end - 1;
  const buckets = Math.max(1, Math.floor(bucketCount));
  const bucketStarts = Array.from({ length: buckets }, (_, bucket) =>
    findIndexAtOrAfter(points, firstTimestamp + (bucket * span) / buckets, innerStart, innerEnd)
  );
  const bucketIndexes = bucketStarts.flatMap((bucketStart, bucket) =>
    pickBucketIndexes(points, bucketStart, bucketStarts[bucket + 1] ?? innerEnd)
  );

  return [start, ...bucketIndexes, end - 1];
}

/** First, lowest, highest and last index of `points[fromIndex..toIndex)`, ascending, without repeats. */
function pickBucketIndexes(points: readonly TimeSeriesPoint[], fromIndex: number, toIndex: number): number[] {
  if (fromIndex >= toIndex) {
    return [];
  }

  let minIndex = fromIndex;
  let maxIndex = fromIndex;

  for (let index = fromIndex + 1; index < toIndex; index += 1) {
    if (points[index].value < points[minIndex].value) {
      minIndex = index;
    } else if (points[index].value > points[maxIndex].value) {
      maxIndex = index;
    }
  }

  return [...new Set([fromIndex, minIndex, maxIndex, toIndex - 1])].sort((a, b) => a - b);
}
